import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type DiretoriaQuerySource = 'erp' | 'cobranca' | 'oficina';

export interface DiretoriaQueryDefinition {
  id: string;
  label: string;
  source: DiretoriaQuerySource;
  sql: string;
}

export function useDiretoriaSqlCatalog(
  billingCompanyId: string,
  dataFrom?: string,
  dataTo?: string,
  compareFrom?: string,
  compareTo?: string
) {
  const { data, isLoading } = useQuery({
    queryKey: ['diretoria-query-catalog', billingCompanyId, dataFrom, dataTo, compareFrom, compareTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (billingCompanyId) params.append('companyId', billingCompanyId);
      if (dataFrom) params.append('dataFrom', dataFrom);
      if (dataTo) params.append('dataTo', dataTo);
      if (compareFrom) params.append('compareFrom', compareFrom);
      if (compareTo) params.append('compareTo', compareTo);
      const response = await api.get(`/reports/diretoria/query-catalog?${params.toString()}`);
      return (response.data.data?.queries ?? []) as DiretoriaQueryDefinition[];
    },
    staleTime: 120_000,
  });

  const byId = useMemo(() => {
    const map = new Map<string, DiretoriaQueryDefinition>();
    for (const q of data ?? []) map.set(q.id, q);
    return map;
  }, [data]);

  const getSql = (queryId: string | undefined, overrideSql?: string | null): string | undefined => {
    if (overrideSql?.trim()) return overrideSql.trim();
    if (!queryId) return undefined;
    return byId.get(queryId)?.sql;
  };

  const getSource = (queryId: string | undefined): DiretoriaQuerySource | undefined => {
    if (!queryId) return undefined;
    return byId.get(queryId)?.source;
  };

  return { getSql, getSource, isLoading };
}
